import './tco-assumptions.scss';

import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AssessmentConfiguration, DescriptionItem, TCOAssumptions } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import {
    Alert, Box, Button, ButtonDropdown, ButtonDropdownProps, Container, Header, Modal, NonCancelableCustomEvent,
    SpaceBetween, Toggle, ToggleProps
} from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { constants } from '../../common/Localization';
import { regions } from '../../common/regions';
import { WEEKS_PER_YEAR } from '../../common/uomConversion';
import { deepCopy } from '../../common/util';
import { TcoContext } from '../../contexts/TcoContext';
import AWSUIFormBuilder, { DistributeMetadataItem } from '../common/AWSUIFormBuilder';
import { defaultConfiguration, emptyAssessmentConfiguration } from './EmptyAssumptions';
import {
    addAlertsToFormField,
    generateDefaultAssumption,
    getDefaultSectionAndFields,
    getRegionAlerts,
    getRelevantAssumptions,
    getVMwareInstanceTypeAlert,
    isSwitchFromChina,
    isSwitchToChina,
    regionSupportRelocate,
    setConfigurationUiHiddenByIdentifier,
    showFields,
    showSections,
    TcoAssumptionInvalidFormMapping
} from './tco-assumptions-modal-helper';
import TcoAssumptionList from './TcoAssumptionList';
import TcoAssumptionSaveOrOverride from './TcoAssumptionSaveOrOverride';
import { loadDescription } from './TCOHelper';

enum ASSUMPTION_ACTIONS_IDS {
    RESTORE_DEFAULTS = 'restoreDefaults',
    SAVE_AS = 'saveAs',
    LOAD = 'load',
}

type TcoAssumptionsModelProps = FlashContextInterface & {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    onSave: (assumptions: AssessmentConfiguration, assumptionName?: string) => void;
}
const tcoService = new TcoService();
const MODIFIED_ASSUMPTIONS_INDICATOR = '*';

function TcoAssumptionsModal(props: TcoAssumptionsModelProps): JSX.Element {
    const location = useLocation().pathname;

    const { tcoLoading, assessment, assessmentToUpdate, assessmentDescription, updateAssessmentToUpdate } = useContext(TcoContext);

    const [ assessmentDescriptionForEditing, setAssessmentDescriptionForEditing ] = useState<DescriptionItem[]>([]);
    const [ awsRegion, setAwsRegion ] = useState<string>(); // Keep track of original aws region
    const [ supportRelocate, setSupportRelocate ] = useState<boolean>(); // Keep track of whether current region supports relocate
    const [ currencyRegionChanged, setCurrencyRegionChanged ] = useState<boolean>(false); // Keep track of whether currecy changed
    const [ formMapping, setFormMapping ] = useState<TcoAssumptionInvalidFormMapping>({}); // Keep track of whether errors exist in forms
    const [ relevantVisibleSections, setRelevantVisibleSections ] = useState<string[] | null>(null);
    const [ showRelevantAssumptions, setShowRelevantAssumptions ] = useState(true);
    const [ defaultVisibleSections, setDefaultVisibleSections ] = useState<string[]>([]);
    const [ defaultVisibleFields, setDefaultVisibleFields ] = useState<string[]>([]);

    // Save and Load
    // assumptionsSetName - used to store the name of the current assumptions set presented in the modal
    const [ assumptionsSetName, setAssumptionsSetName ] = useState(assessment?.assumptionName ?? constants.DEFAULT_ASSUMPTION_SET_NAME);
    const [ showLoadAssumptions, setShowLoadAssumptions ] = useState(false);
    const [ showSaveOrOverride, setShowSaveOrOverride ] = useState(false);

    const cancelAssumptionActions = () => {
        setShowLoadAssumptions(false);
        setShowSaveOrOverride(false);
        setHideConfiguration(false);
    };

    // Modified assumptions used to track form changes
    const [ modifiedAssumptions, setModifiedAssumptions ] = useState<AssessmentConfiguration>(deepCopy(emptyAssessmentConfiguration));
    // Init assumptions used to generate form builder
    const [ initAssumptions, setInitAssumptions ] = useState<AssessmentConfiguration>(deepCopy(emptyAssessmentConfiguration));

    const [ showRestoreDefaultConfirmation, setshowRestoreDefaultConfirmation ] = useState(false);
    const [ hideConfiguration, setHideConfiguration ] = useState(false);

    useEffect(() => {
        if (assessmentDescription) {
            const copiedDefaultAssumption: DescriptionItem[] = deepCopy(assessmentDescription);
            const relevantAssumption = getRelevantAssumptions(location);
            const { defaultFields, defaultSections, defaultFormMapping } = getDefaultSectionAndFields(assessmentDescription);
            setRelevantVisibleSections(relevantAssumption);
            setDefaultVisibleFields(defaultFields);
            setDefaultVisibleSections(defaultSections);
            setFormMapping(defaultFormMapping);
            
            if (relevantAssumption) {
                setAssessmentDescriptionForEditing(showSections(copiedDefaultAssumption, relevantAssumption));
            } else {
                setAssessmentDescriptionForEditing(copiedDefaultAssumption);
            }
        }
    }, [ assessmentDescription, location ]);

    useEffect(() => {
        if (assessment) {
            const copiedInitialConfiguration: AssessmentConfiguration = deepCopy(assessment.configuration);
            setModifiedAssumptions(copiedInitialConfiguration);
            setInitAssumptions(copiedInitialConfiguration);
            setAwsRegion(copiedInitialConfiguration.tcoAssumptions.awsRegion);
            setSupportRelocate(regionSupportRelocate(copiedInitialConfiguration.tcoAssumptions.awsRegion, regions));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    // Listen to supportRelocate, update description accordingly
    useEffect(() => {
        if (supportRelocate == undefined) {
            return;
        }
        
        const copiedDescription: DescriptionItem[] = deepCopy(assessmentDescriptionForEditing);

        setConfigurationUiHiddenByIdentifier(copiedDescription, 'tcoAssumptions', 'relocateOn', !supportRelocate);
        setAssessmentDescriptionForEditing(copiedDescription);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ supportRelocate ]);

    const dismissModal = (): void => props.setVisible(false);

    const clickShowRelevantAssumptions = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>): void => {
        setShowRelevantAssumptions(event.detail.checked);
        if (event.detail.checked && relevantVisibleSections) {
            setAssessmentDescriptionForEditing(showSections(assessmentDescriptionForEditing, relevantVisibleSections));
        } else {
            setAssessmentDescriptionForEditing(showSections(assessmentDescriptionForEditing, defaultVisibleSections));
        }
    };

    const restoreDefaults = () => {
        setshowRestoreDefaultConfirmation(true);
        setModifiedAssumptions(defaultConfiguration);
        setInitAssumptions(defaultConfiguration);
        setAssumptionsSetName(constants.DEFAULT_ASSUMPTION_SET_NAME);
    };

    const save = async (assumptionName?: string) => {
        // Check if errors exist in each forms
        if (Object.values(formMapping).includes(false)) {
            props.flash.alert(FlashType.error, constants.INVALID_ASSUMPTION_INPUT, constants.INVALID_ASSUMPTION_TYPE);
            props.setVisible(false);
            return;
        }

        // Check if tco duration year is valid
        if (modifiedAssumptions.tcoAssumptions.durationYears > modifiedAssumptions.tcoAssumptions.refreshmentDurationYears) {
            props.flash.alert(FlashType.error, constants.INVALID_TCO_DURATION, constants.INVALID_ASSUMPTION_TYPE);
            props.setVisible(false);
            return;
        }

        // Check if migration project duration is valid
        const migrationDuration = Number(modifiedAssumptions.migrationToolsAndSchedule.projectDurationWeeks) / WEEKS_PER_YEAR;
        if (migrationDuration > modifiedAssumptions.tcoAssumptions.durationYears) {
            props.flash.alert(FlashType.error, constants.INVALID_MIGRATION_DURATION, constants.INVALID_ASSUMPTION_TYPE);
            props.setVisible(false);
            return;
        }

        // Check if at least 1 valid EBS type is selected
        if (modifiedAssumptions.awsStorage.awsEBSTypes.length < 1) {
            props.flash.alert(FlashType.error, constants.INVALID_EBS_TYPES, constants.INVALID_ASSUMPTION_TYPE);
            props.setVisible(false);
            return;
        }

        // Check assessment input
        if (assessmentToUpdate && assessmentToUpdate.input.migration) {
            // migration distribution validation
            if (assessmentToUpdate.input.migration.migrationCompletionDistribution &&
                assessmentToUpdate.input.migration.migrationCompletionDistribution.length > 0) {
                const sum = assessmentToUpdate.input.migration.migrationCompletionDistribution.reduce((sum, dist) => sum + dist, 0);
                if (sum != 100) {
                    props.flash.alert(FlashType.error, constants.INVALID_MIGRATION_PLAN_DISTRIBUTION, constants.INVALID_ASSUMPTION_TYPE);
                    props.setVisible(false);
                    return;
                }
            }

            // migration strategy input validation
            if (assessmentToUpdate.input.migration.migrationStrategySummary) {
                if (assessmentToUpdate.input.migration.migrationStrategySummary.some(strategy => {
                    const count = strategy.count;
                    return count < 0 || !Number.isInteger(count);
                })) {
                    props.flash.alert(FlashType.error, constants.INVALID_MIGRATION_PLAN_DISTRIBUTION, constants.INVALID_ASSUMPTION_TYPE);
                    props.setVisible(false);
                    return;
                }
            }
        }

        let copiedModifiedAssumption = deepCopy(modifiedAssumptions);
        if (!supportRelocate) {
            // If current region does not support relocate, always set relocateOn = false
            copiedModifiedAssumption.tcoAssumptions.relocateOn = false;
        }

        if (currencyRegionChanged) {
            const newRegion: string = copiedModifiedAssumption.tcoAssumptions.awsRegion;
            const newRegionDescriptions = await loadDescription(tcoService, newRegion);
            const newRegionDefaultAssumption = generateDefaultAssumption(newRegionDescriptions);
            copiedModifiedAssumption = {
                ...newRegionDefaultAssumption,
                tcoAssumptions: copiedModifiedAssumption.tcoAssumptions,
            };
        }
        if (assessmentToUpdate) {
            updateAssessmentToUpdate({
                ...assessmentToUpdate,
                assumptionName: assumptionName ?? assumptionsSetName,
                configuration: copiedModifiedAssumption,
            });
        }

        props.onSave(copiedModifiedAssumption, assumptionName ?? assumptionsSetName);
        props.setVisible(false);
    };

    function onAssumptionsUpdate<T>(key: string) {
        const copiedAssumption: AssessmentConfiguration = deepCopy(modifiedAssumptions);
        const oldRegion = awsRegion;
    
        return (updatedAssumption: T, isFormValid?: boolean): void => {
            if (isFormValid != undefined) {
                const copiedInvalidFormMapping: TcoAssumptionInvalidFormMapping = deepCopy(formMapping);
                copiedInvalidFormMapping[key] = isFormValid;
                setFormMapping(copiedInvalidFormMapping);
            }
            
            const copiedDescription: DescriptionItem[] = deepCopy(assessmentDescriptionForEditing);
            
            // region update
            const newRegion = (updatedAssumption as any).awsRegion;
            if (newRegion && oldRegion) {
                const alertMsgs: string[] = getRegionAlerts(newRegion, oldRegion, copiedAssumption.vmwareCloud.instanceType, regions);
                const currencyChanged = isSwitchFromChina(newRegion, oldRegion) || isSwitchToChina(newRegion, oldRegion);

                // If new region supports relocate and no currency change, display relocate toggle
                const relocateSupported = regionSupportRelocate(newRegion, regions) && !currencyChanged;
                setSupportRelocate(relocateSupported);

                if (currencyChanged) {
                    // If currency changed, only display 'awsRegion' field
                    showFields(copiedDescription, ['tcoAssumptions'], ['awsRegion']);
                } else if (currencyRegionChanged) {
                    // If currency changed before, but not now, restore to default
                    const visibleSections = showRelevantAssumptions ? (relevantVisibleSections ?? defaultVisibleSections) : defaultVisibleSections;
                    showFields(copiedDescription, visibleSections, defaultVisibleFields);
                }

                // Double check relocate after currency changed
                setConfigurationUiHiddenByIdentifier(copiedDescription, 'tcoAssumptions', 'relocateOn', !relocateSupported);

                addAlertsToFormField(copiedDescription, 'tcoAssumptions', 'awsRegion', alertMsgs);
                setAssessmentDescriptionForEditing(copiedDescription);
                setCurrencyRegionChanged(currencyChanged);
            }

            const instanceType = (updatedAssumption as any).instanceType;
            if (instanceType && oldRegion) { // if instance type updated, lets also rerun the region update code to update the alerts
                addAlertsToFormField(
                    copiedDescription, 
                    'tcoAssumptions', 
                    'awsRegion', 
                    getRegionAlerts(modifiedAssumptions.tcoAssumptions.awsRegion, oldRegion, instanceType, regions)
                );
            }

            // check if region updated OR instance type updated
            if (newRegion || instanceType) { // now, if either updated, lets update the vmwareCloud alerts
                const regionToCheckVmwareCompatibility = newRegion ?? copiedAssumption.tcoAssumptions.awsRegion;
                const instanceTypeToCheckVmwareCompatibility = instanceType ?? copiedAssumption.vmwareCloud.instanceType;

                const alertMsg = getVMwareInstanceTypeAlert(regionToCheckVmwareCompatibility, instanceTypeToCheckVmwareCompatibility, regions);

                addAlertsToFormField(copiedDescription, 'vmwareCloud', 'instanceType', alertMsg ? [alertMsg] : []);
            }

            // ebs type update
            const selectedEbsTypes: string[] = (updatedAssumption as any).awsEBSTypes;
            if (selectedEbsTypes) {
                const alertMsgs = selectedEbsTypes.length < 1 ? [constants.INVALID_EBS_TYPES_MSG] : [];
                addAlertsToFormField(copiedDescription, 'awsStorage', 'awsEBSTypes', alertMsgs);
                setAssessmentDescriptionForEditing(copiedDescription);
            }

            // Check duration years against refreshment duration years
            if (isTCOAssumptions(updatedAssumption)) {
                const { durationYears, refreshmentDurationYears} = updatedAssumption;
                const alertMsgs = durationYears > refreshmentDurationYears ? [constants.INVALID_DURATION_INPUT] : [];
                addAlertsToFormField(copiedDescription, 'tcoAssumptions', 'durationYears', alertMsgs);
                setAssessmentDescriptionForEditing(copiedDescription);
            }

            const newAssumptionSet = {...copiedAssumption, [key]: updatedAssumption};
            if (JSON.stringify(newAssumptionSet) !== JSON.stringify(copiedAssumption)) {
                setModifiedAssumptions(
                    {...copiedAssumption, [key]: updatedAssumption}
                );
                setAssumptionsSetName(assumptionsSetName.replace(MODIFIED_ASSUMPTIONS_INDICATOR, '') + MODIFIED_ASSUMPTIONS_INDICATOR);
            }
        };
    };

    // Type guard for TCOAssumptions
    const isTCOAssumptions = (obj: any): obj is TCOAssumptions => {
        return typeof obj.durationYears !== undefined;
    };

    const onActionClicked = (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        switch (event.detail.id) {
            case ASSUMPTION_ACTIONS_IDS.RESTORE_DEFAULTS:
                setShowSaveOrOverride(false);
                setShowLoadAssumptions(false);
                setHideConfiguration(false);
                restoreDefaults();
                break;
            case ASSUMPTION_ACTIONS_IDS.SAVE_AS:
                setShowSaveOrOverride(true);
                setShowLoadAssumptions(false);
                setHideConfiguration(true);
                break;
            case ASSUMPTION_ACTIONS_IDS.LOAD:
                setShowSaveOrOverride(false);
                setShowLoadAssumptions(true);
                setHideConfiguration(true);
                break;
        }
    };

    const ASSUMPTIONS_ACTIONS: ButtonDropdownProps.Items = [
        { id: ASSUMPTION_ACTIONS_IDS.RESTORE_DEFAULTS, text: constants.RESTORE_DEFAULTS },
        { id: ASSUMPTION_ACTIONS_IDS.SAVE_AS, text: constants.SAVE_AS_BUTTON_TEXT },
        { id: ASSUMPTION_ACTIONS_IDS.LOAD, text: constants.LOAD_BUTTON },
    ];

    const actions = (
        <Box float='right'>
            <ButtonDropdown
                onItemClick={onActionClicked}
                items={ASSUMPTIONS_ACTIONS}>
                {constants.ACTION_BUTTON_TEXT}
            </ButtonDropdown>
        </Box>
    );

    const header = (
        <Header description={constants.MODIFY_ASSUMPTIONS_DESCR_1}> 
            {constants.MODIFY_ASSUMPTIONS} <em>({assumptionsSetName})</em>
        </Header>
    );

    const footer = () => {
        return hideConfiguration ? ( 
            <></>
        ) : (
            <Box>
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='s'>
                        <Button disabled={tcoLoading} onClick={dismissModal}>{constants.CANCEL}</Button>
                        <Button data-testid='btn-apply' loading={tcoLoading} variant='primary' onClick={() => save()}>{constants.APPLY}</Button>
                    </SpaceBetween>
                </Box>
            </Box>
        );
    };

    const saveAssumption = (assumptionName: string) => {
        setAssumptionsSetName(assumptionName ?? constants.DEFAULT_ASSUMPTION_SET_NAME);
        save(assumptionName ?? constants.DEFAULT_ASSUMPTION_SET_NAME);
    };

    const loadAssumption = (newAssumption: AssessmentConfiguration, assumptionName: string) => {
        props.setVisible(false);
        if (assessmentToUpdate) {
            updateAssessmentToUpdate({
                ...assessmentToUpdate,
                assumptionName, 
                configuration: newAssumption,
            });
        }

        props.onSave(newAssumption, assumptionName);
    };

    const allAssumptionsForms = () => {
        const configuration = initAssumptions;  
        return configuration ?
            assessmentDescriptionForEditing.map((description: DescriptionItem) => {
                if (description.uiHidden) {
                    return null;
                }
                const key = description.identifier;
                if (configuration.hasOwnProperty(key)) {
                    const items = configuration[key as keyof AssessmentConfiguration];
                    type AssumptionType = typeof items;
                    const metadata = description as DistributeMetadataItem<AssumptionType>;
                    return (
                        <SpaceBetween size='s' key={key}>
                            <AWSUIFormBuilder<AssumptionType>
                                key={key}
                                item={items}
                                metadata={[metadata]}
                                onUpdate={onAssumptionsUpdate<AssumptionType>(key)}
                                className='tcoAssumptionsModal'
                                hasActions={false}
                            />
                        </SpaceBetween>
                    );
                } else {
                    return null;
                }
            }) : null;
    };

    const baseAssumptionsSetName = assumptionsSetName.replace(MODIFIED_ASSUMPTIONS_INDICATOR, '');
    const isDefaultAssumptionsSet = baseAssumptionsSetName === constants.DEFAULT_ASSUMPTION_SET_NAME;

    return (
        <Modal
            data-testid='TcoAssumptionsModal'
            header={header}
            visible={props.visible}
            onDismiss={dismissModal}
            footer={footer()}
            size='large'
        >
            <SpaceBetween size='m'>
                {
                    !showLoadAssumptions && !showSaveOrOverride && (
                        <Container disableContentPaddings header={
                            <Header variant='h3'
                                description={constants.MODIFY_ASSUMPTIONS_DESCR_2}
                                actions={actions}
                            />}
                        />
                    )}
                <div className={hideConfiguration ? 'hidden' : ''}>
                    <Toggle
                        checked={showRelevantAssumptions}
                        onChange={clickShowRelevantAssumptions}
                    >
                        {constants.RELEVANT_ASSUMPTIONS}
                    </Toggle>
                </div>
                {
                    showLoadAssumptions && (
                        <TcoAssumptionList
                            configuration={modifiedAssumptions}
                            cancel={cancelAssumptionActions}
                            onConfirmLoadAssumption={loadAssumption}
                            isSaveAsExisting={false}
                            title={constants.LOAD_ASSUMPTION_TITLE}
                        />
                    )}
                {
                    showSaveOrOverride && (
                        <TcoAssumptionSaveOrOverride
                            configuration={modifiedAssumptions}
                            cancel={cancelAssumptionActions}
                            onSaveAssumption={saveAssumption}
                            saveAsNew={isDefaultAssumptionsSet}
                            assumptionName={isDefaultAssumptionsSet ? '' : baseAssumptionsSetName}
                        />
                    )}
                <div className={hideConfiguration ? 'hidden' : 'assumptionsModal'}>
                    {allAssumptionsForms()}
                </div>
                {showRestoreDefaultConfirmation && !showLoadAssumptions && !showSaveOrOverride && (
                    <Alert type='warning'>
                        {constants.RESTORE_DEFAULTS_ALERT}
                        <br></br>
                        {constants.RESTORE_DEFAULTS_ACTION_1}<strong>{constants.CANCEL}</strong>.&nbsp;
                        {constants.RESTORE_DEFAULTS_ACTION_2}<strong>{constants.APPLY}</strong>.
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
};

export default withFlashContext(TcoAssumptionsModal);
